/*
  Cart drawer extras.

  1. Free shipping meter, read from the live cart and refreshed whenever the
     drawer re-renders.
  2. One-tap upsell: adds a product without closing the drawer, then hands the
     response to Dawn's own cart drawer so the whole thing re-renders exactly
     as it does for any other add.

  Delegated listeners and a MutationObserver, because Dawn replaces the
  drawer's innerHTML on every cart change, which would kill bound handlers.
*/
(function () {
  const drawer = () => document.querySelector('cart-drawer');

  function formatMoney(cents, format) {
    const whole = cents % 100 === 0;
    const amount = whole ? String(cents / 100) : (cents / 100).toFixed(2);
    const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/g, withCommas)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withCommas.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(cents / 100)));
  }

  async function refreshShipping() {
    const wrap = document.querySelector('[data-cd-ship]');
    if (!wrap) return;

    const threshold = parseInt(wrap.dataset.threshold || '0', 10);
    if (!threshold) return;

    let cart;
    try {
      cart = await (await fetch('/cart.js')).json();
    } catch (e) {
      return;
    }

    const total = cart.total_price || 0;
    const remaining = threshold - total;
    const text = wrap.querySelector('[data-cd-ship-text]');
    const pct = Math.min(100, Math.round((total / threshold) * 100));

    wrap.style.setProperty('--cd-ship-fill', pct + '%');

    if (!text) return;

    if (remaining <= 0) {
      text.textContent = wrap.dataset.unlocked || 'Free shipping unlocked';
      wrap.classList.add('is-unlocked');
    } else {
      text.innerHTML =
        (wrap.dataset.prefix || 'You are') +
        ' <strong>' + formatMoney(remaining, wrap.dataset.moneyFormat) + '</strong> ' +
        (wrap.dataset.suffix || 'from free shipping');
      wrap.classList.remove('is-unlocked');
    }
  }

  async function addUpsell(button) {
    const row = button.closest('[data-cd-upsell-item]');
    if (!row) return;

    const select = row.querySelector('[data-cd-upsell-variant]');
    const id = select ? select.value : row.dataset.variantId;
    if (!id) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = button.dataset.addingLabel || 'Adding';

    const cart = drawer();
    const body = { items: [{ id: Number(id), quantity: 1 }] };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
        body: JSON.stringify(body),
      }).then((r) => r.json());

      if (response.status) {
        button.textContent = button.dataset.soldOutLabel || 'Unavailable';
        return;
      }

      if (cart && typeof cart.renderContents === 'function') {
        cart.renderContents(response);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.warn('[cart upsell] add failed', e);
      button.textContent = original;
    } finally {
      button.disabled = false;
      // Dawn may have replaced this node already; only restore if it survived.
      if (document.contains(button)) button.textContent = original;
      refreshShipping();
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-cd-upsell-add]');
    if (!button) return;
    event.preventDefault();
    addUpsell(button);
  });

  // Dawn rewrites the drawer's contents wholesale, so re-run on every change.
  function watch() {
    const cart = drawer();
    if (!cart) return;
    new MutationObserver(() => refreshShipping()).observe(cart, {
      childList: true,
      subtree: true,
    });
    refreshShipping();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();

/*
  Upsell rail arrows.

  The rail is a horizontal scroller, which a phone drives with a swipe and a
  mouse cannot drive at all. These arrows step it one card at a time and stay
  out of the way when everything already fits.

  Delegated and re-checked on cart updates, because the drawer replaces its own
  markup after every add.
*/
(function () {
  function rail(root) {
    return {
      list: root.querySelector('[data-upsell-list]'),
      nav: root.querySelector('[data-upsell-nav]'),
      prev: root.querySelector('[data-upsell-prev]'),
      next: root.querySelector('[data-upsell-next]'),
    };
  }

  function step(list) {
    var card = list.querySelector('.cd-upsell__item');
    if (!card) return list.clientWidth;
    var styles = window.getComputedStyle(list);
    return card.getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
  }

  function sync(root) {
    var r = rail(root);
    if (!r.list || !r.nav) return;

    // 2px of slack: sub-pixel widths otherwise leave the arrows permanently on.
    var overflows = r.list.scrollWidth > r.list.clientWidth + 2;
    r.nav.hidden = !overflows;
    if (!overflows) return;

    var max = r.list.scrollWidth - r.list.clientWidth;
    if (r.prev) r.prev.disabled = r.list.scrollLeft <= 2;
    if (r.next) r.next.disabled = r.list.scrollLeft >= max - 2;
  }

  function syncAll() {
    document.querySelectorAll('.cd-upsell').forEach(sync);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-upsell-prev], [data-upsell-next]');
    if (!button) return;

    var root = button.closest('.cd-upsell');
    var list = root && root.querySelector('[data-upsell-list]');
    if (!list) return;

    var direction = button.hasAttribute('data-upsell-next') ? 1 : -1;
    list.scrollBy({ left: step(list) * direction, behavior: 'smooth' });
  });

  document.addEventListener(
    'scroll',
    function (event) {
      var list = event.target;
      if (list && list.matches && list.matches('[data-upsell-list]')) {
        sync(list.closest('.cd-upsell'));
      }
    },
    true
  );

  window.addEventListener('resize', syncAll);
  document.addEventListener('DOMContentLoaded', syncAll);
  // The drawer rewrites itself after every add, so re-check once it settles.
  document.addEventListener('cart:refresh', function () {
    window.setTimeout(syncAll, 60);
  });
  new MutationObserver(function () {
    window.setTimeout(syncAll, 60);
  }).observe(document.documentElement, { childList: true, subtree: true });

  syncAll();
})();
